import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Bell, Briefcase } from "lucide-react";
import { AccountLayout } from "@/components/candidate/AccountLayout";
import { apiFetch, formatDate, StatusPill } from "@/components/candidate/shared";

const STATS = [
    ["total", "Total applications"],
    ["submitted", "Submitted"],
    ["under_review", "Under review"],
    ["shortlisted", "Shortlisted"],
    ["interview", "Interview"],
    ["selected", "Selected"],
    ["offer", "Offer"],
    ["placement", "Placement"],
    ["rejected", "Rejected"],
];

export default function CandidateDashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        apiFetch("/candidate/dashboard")
            .then((r) => (r.ok ? r.json() : null))
            .then(setData)
            .catch(() => {});
    }, []);

    return (
        <AccountLayout
            title={data ? `Welcome back, ${data.name.split(" ")[0]}` : "Welcome back"}
            subtitle="Your international career at a glance."
        >
            {!data ? (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest border-t-transparent" />
            ) : (
                <div className="space-y-14" data-testid="candidate-dashboard">
                    <div className="border border-ink/10 bg-sand p-7 sm:p-8">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-forest">
                                    Profile completion
                                </p>
                                <p className="mt-2 font-serif text-3xl font-medium">{data.completion}%</p>
                            </div>
                            <a
                                href="/account/profile"
                                data-testid="dash-complete-profile"
                                className="inline-flex items-center gap-2 border border-forest/30 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory"
                            >
                                Complete your profile
                                <ArrowRight size={14} strokeWidth={1.5} />
                            </a>
                        </div>
                        <div className="mt-5 h-1.5 w-full bg-ink/10">
                            <div
                                data-testid="dash-completion-bar"
                                className="h-full bg-forest transition-all duration-700"
                                style={{ width: `${data.completion}%` }}
                            />
                        </div>
                        <p className="mt-3 text-xs text-ink/50">
                            A complete profile helps our recruitment team match you to the right roles.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-4">
                        {STATS.map(([key, label]) => (
                            <div key={key} data-testid={`dash-stat-${key}`} className="bg-ivory p-5">
                                <p className="font-serif text-3xl font-medium text-ink">
                                    {key === "total" ? data.total : data.counts[key] || 0}
                                </p>
                                <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.18em] text-ink/50">
                                    {label}
                                </p>
                            </div>
                        ))}
                    </div>

                    <section>
                        <div className="flex items-center justify-between">
                            <h2 className="font-serif text-xl font-medium tracking-tight">Recent applications</h2>
                            <a href="/account/applications" data-testid="dash-all-applications" className="text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:text-champagne">
                                View all
                            </a>
                        </div>
                        <div className="mt-5">
                            {data.recent.length === 0 ? (
                                <p className="border-t border-ink/10 py-10 text-sm text-ink/50">
                                    No applications yet —{" "}
                                    <a href="/jobs" className="font-medium text-forest">browse open roles</a>{" "}
                                    and apply.
                                </p>
                            ) : (
                                data.recent.map((app) => (
                                    <a
                                        key={app.id}
                                        href={`/account/applications/${app.id}`}
                                        data-testid={`dash-app-${app.id}`}
                                        className="grid grid-cols-[1fr_auto] items-center gap-4 border-t border-ink/10 px-2 py-4 transition-colors hover:bg-sand"
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate font-medium text-ink">{app.job_title}</span>
                                            <span className="block text-xs text-ink/50">
                                                {app.job_company} · {app.reference} · {formatDate(app.created_at)}
                                            </span>
                                        </span>
                                        <StatusPill status={app.status} />
                                    </a>
                                ))
                            )}
                            <div className="border-t border-ink/10" />
                        </div>
                    </section>

                    <section>
                        <div className="flex items-center justify-between">
                            <h2 className="flex items-center gap-2 font-serif text-xl font-medium tracking-tight">
                                <Briefcase size={18} strokeWidth={1.5} className="text-forest" />
                                Recommended for you
                            </h2>
                            <a href="/jobs" data-testid="dash-browse-jobs" className="text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:text-champagne">
                                All jobs
                            </a>
                        </div>
                        <div className="mt-5 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2">
                            {data.recommended.map((job) => (
                                <a
                                    key={job.id}
                                    href={`/jobs/${job.slug}`}
                                    data-testid={`dash-rec-${job.slug}`}
                                    className="group bg-ivory p-6 transition-colors hover:bg-sand"
                                >
                                    <p className="font-serif text-lg font-medium tracking-tight text-ink group-hover:text-forest">
                                        {job.title}
                                    </p>
                                    <p className="mt-1 text-xs text-ink/50">
                                        {job.company} · {job.city ? `${job.city}, ` : ""}{job.country}
                                    </p>
                                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-forest">
                                        View role
                                        <ArrowUpRight size={13} strokeWidth={1.5} />
                                    </span>
                                </a>
                            ))}
                        </div>
                    </section>

                    <div className="grid gap-10 lg:grid-cols-2">
                        <section>
                            <div className="flex items-center justify-between">
                                <h2 className="font-serif text-xl font-medium tracking-tight">Saved jobs</h2>
                                <a href="/account/saved" data-testid="dash-saved-link" className="text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:text-champagne">
                                    Manage
                                </a>
                            </div>
                            <div className="mt-5">
                                {data.saved.length === 0 ? (
                                    <p className="border-t border-ink/10 py-8 text-sm text-ink/50">
                                        No saved jobs yet — tap the bookmark on any role to save it.
                                    </p>
                                ) : (
                                    data.saved.map((job) => (
                                        <a
                                            key={job.id}
                                            href={`/jobs/${job.slug}`}
                                            data-testid={`dash-saved-${job.slug}`}
                                            className="block border-t border-ink/10 px-2 py-4 transition-colors hover:bg-sand"
                                        >
                                            <span className="block truncate font-medium text-ink">{job.title}</span>
                                            <span className="text-xs text-ink/50">{job.company} · {job.country}</span>
                                        </a>
                                    ))
                                )}
                                <div className="border-t border-ink/10" />
                            </div>
                        </section>

                        <section className="border border-ink/10 bg-night p-7 text-ivory sm:p-8">
                            <h2 className="flex items-center gap-2 font-serif text-xl font-medium tracking-tight">
                                <Bell size={18} strokeWidth={1.5} className="text-champagne" />
                                Job alerts
                            </h2>
                            <p className="mt-3 text-sm leading-relaxed text-ivory/65">
                                {data.alerts.subscribed
                                    ? data.alerts.enabled
                                        ? `Alerts are on${data.alerts.profession ? ` for “${data.alerts.profession}”` : ""}. Matching roles land in your inbox as they're published.`
                                        : "Your alerts are paused. Turn them back on to hear about matching roles."
                                    : "Subscribe once and matching international roles come straight to your inbox."}
                            </p>
                            <a
                                href="/account/alerts"
                                data-testid="dash-alerts-link"
                                className="mt-6 inline-flex items-center gap-2 border border-ivory/30 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:border-champagne hover:text-champagne"
                            >
                                Manage alerts
                                <ArrowRight size={14} strokeWidth={1.5} />
                            </a>
                        </section>
                    </div>
                </div>
            )}
        </AccountLayout>
    );
}
